/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref as databaseRef, get, update } from 'firebase/database';
import { dbRealtime, storage } from '../environments/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { CogIcon, CheckIcon, PencilIcon } from '@heroicons/react/24/outline';
import countries from '../components/countries';
import { X } from 'lucide-react';

export default function ProfileSettings() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    avatarUrl: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        const snapshot = await get(databaseRef(dbRealtime, `users/${currentUser.uid}/profile`));
        if (snapshot.exists()) {
          const userProfile = snapshot.val();
          setProfile({
            firstName: userProfile.firstName || '',
            lastName: userProfile.lastName || '',
            phone: userProfile.phone || '',
            country: userProfile.country || '',
            avatarUrl: userProfile.avatarUrl || ''
          });
          // If profile exists, disable edit mode
          setEditMode(false);
        } else {
          // If no profile exists, enable edit mode
          setEditMode(true);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !currentUser) return null;
    
    try {
      const fileRef = storageRef(storage, `avatars/${currentUser.uid}/${avatarFile.name}`);
      const snapshot = await uploadBytes(fileRef, avatarFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return null;
    }
  };

  const saveProfile = async () => {
    if (!currentUser) return;
    
    try {
      setIsSaving(true);
      
      // Upload avatar if a new one was selected
      let avatarUrl = profile.avatarUrl;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }

      const profileData = {
        ...profile,
        avatarUrl: avatarUrl || profile.avatarUrl
      };

      await update(
        databaseRef(dbRealtime, `users/${currentUser.uid}`),
        { profile: profileData }
      );
      
      // Update local state with the new avatar URL if it was uploaded
      if (avatarUrl) {
        setProfile(prev => ({ ...prev, avatarUrl }));
      }
      
      setShowSuccess(true);
      setEditMode(false);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEdit = () => {
    // Reset to original profile data
    const originalProfile = { ...profile };
    setProfile(originalProfile);
    setAvatarPreview('');
    setAvatarFile(null);
    setEditMode(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Profile Settings</h1>
                <p className="text-gray-500 mt-1">
                  {editMode ? 'Complete your profile information' : 'Your profile information'}
                </p>
              </div>
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition flex items-center"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={saveProfile}
                    disabled={isSaving}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition flex items-center ${
                      isSaving ? 'bg-purple-400' : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Save Profile
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="bg-green-50 px-6 py-4 flex items-center">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700">Profile saved successfully!</span>
            </div>
          )}

          {/* Profile Content */}
          <div className="px-6 py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Avatar Section */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  {profile.avatarUrl || avatarPreview ? (
                    <img
                      src={avatarPreview || profile.avatarUrl}
                      alt="Profile"
                      className="w-32 h-32 rounded-full object-cover border-4 border-purple-100"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-4xl font-bold border-4 border-purple-200">
                      {profile.firstName ? profile.firstName.charAt(0) : 'U'}
                    </div>
                  )}
                  {editMode && (
                    <label className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md cursor-pointer hover:bg-gray-50">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <PencilIcon className="h-5 w-5 text-purple-600" />
                    </label>
                  )}
                </div>
                {editMode && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Click on the pencil to upload a profile photo
                  </p>
                )}
              </div>

              {/* Profile Form */}
              <div className="flex-1 space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={profile.firstName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm p-2 border"
                        required
                      />
                    ) : (
                      <p className="mt-1 text-gray-900 font-medium">{profile.firstName || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={profile.lastName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm p-2 border"
                      />
                    ) : (
                      <p className="mt-1 text-gray-900 font-medium">{profile.lastName || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  {editMode ? (
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={profile.phone}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm p-2 border"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900 font-medium">{profile.phone || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  {editMode ? (
                    <select
                      name="country"
                      id="country"
                      value={profile.country}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm p-2 border"
                    >
                      <option value="">Select a country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-900 font-medium">{profile.country || 'Not provided'}</p>
                  )}
                </div>

                {!editMode && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700">User ID</h3>
                    <p className="mt-1 text-sm text-gray-500 break-all">{currentUser?.uid}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Help Text */}
        {editMode && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-medium text-gray-900 mb-2">Profile Information</h3>
            <p className="text-gray-600 text-sm">
              Please provide your profile information. Once saved, this information cannot be changed often.
              Make sure all details are accurate before saving.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}